import React, { createContext, useContext, useMemo, useCallback, useState } from 'react';
import { Alert, AlertVariant } from './Alert';

type ShowOptions = {
  /** Row 1 title (next to icon). If omitted, a default per variant is used. */
  title?: React.ReactNode;
  /** Row 2 description */
  message: React.ReactNode;
  variant?: AlertVariant;       // 'success' | 'warning' | 'failure'
  autoCloseMs?: number;         // default: 10000
  ariaLabel?: string;
  /** Row 3 cancel button text; default: 'Cancel' */
  closeLabel?: string;
  /** Row 3 optional cancel icon (left to label) */
  closeIcon?: React.ReactNode;
};

type AlertContextType = {
  showAlert: (options: ShowOptions) => void;
  showSuccess: (options: Omit<ShowOptions, 'variant'>) => void;
  showWarning: (options: Omit<ShowOptions, 'variant'>) => void;
  showFailure: (options: Omit<ShowOptions, 'variant'>) => void;
  closeAlert: () => void;
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

// Optional: default titles when caller doesn't pass one
const defaultTitle = (variant: AlertVariant) => {
  switch (variant) {
    case 'success': return 'Success';
    case 'warning': return 'Warning';
    case 'failure': return 'Error';
  }
};

export const AlertProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false);

  const [state, setState] = useState<Required<Omit<ShowOptions, 'closeIcon'>> & {
    closeIcon?: React.ReactNode;
    variant: AlertVariant;
  }>({
    title: 'Success',
    message: '',
    variant: 'success',
    autoCloseMs: 10000,
    ariaLabel: '',
    closeLabel: 'Cancel',
    closeIcon: undefined,
  });

  const showAlert = useCallback((options: ShowOptions) => {
    const variant = options.variant ?? 'success';
    setState({
      title: options.title ?? defaultTitle(variant),
      message: options.message,
      variant,
      autoCloseMs: options.autoCloseMs ?? 10000,
      ariaLabel: options.ariaLabel as string,
      closeLabel: options.closeLabel ?? 'Cancel',
      closeIcon: options.closeIcon,
    });
    setOpen(true);
  }, []);

  // Convenience helpers
  const showSuccess = useCallback((options: Omit<ShowOptions, 'variant'>) => {
    showAlert({ ...options, variant: 'success' });
  }, [showAlert]);

  const showWarning = useCallback((options: Omit<ShowOptions, 'variant'>) => {
    showAlert({ ...options, variant: 'warning' });
  }, [showAlert]);

  const showFailure = useCallback((options: Omit<ShowOptions, 'variant'>) => {
    showAlert({ ...options, variant: 'failure' });
  }, [showAlert]);

  const closeAlert = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ showAlert, showSuccess, showWarning, showFailure, closeAlert }),
    [showAlert, showSuccess, showWarning, showFailure, closeAlert]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      <Alert
        open={open}
        onClose={closeAlert}
        title={state.title}
        message={state.message}
        variant={state.variant}
        autoCloseMs={state.autoCloseMs}
        ariaLabel={state.ariaLabel}
        closeLabel={state.closeLabel}
        closeIcon={state.closeIcon}
      />
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within an AlertProvider');
  return ctx;
};